/**
 * AuthGate Component
 * Handles authentication state display:
 * - Shows loading spinner during silent auth
 * - Renders children when authenticated
 * - Shows "Continue" button when interaction is required
 */

import { type ReactNode } from 'react'
import {
  Button,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { PersonRegular } from '@fluentui/react-icons'
import { useAuthContext } from './AuthProvider'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalL,
    minHeight: '200px',
    padding: tokens.spacingVerticalXXL,
  },
  message: {
    textAlign: 'center',
    color: tokens.colorNeutralForeground2,
  },
  teamsHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
})

interface AuthGateProps {
  children: ReactNode
  /** Custom loading message */
  loadingMessage?: string
  /** Custom prompt message when interaction is required */
  promptMessage?: string
  /** Custom button text */
  buttonText?: string
}

/**
 * AuthGate wraps content that requires authentication
 * Provides seamless SSO experience in Teams and graceful fallback in browser
 */
export function AuthGate({
  children,
  loadingMessage = 'Signing you in...',
  promptMessage = 'Sign in to continue',
  buttonText = 'Continue',
}: AuthGateProps) {
  const styles = useStyles()
  const {
    isTeams,
    requiresInteraction,
    silentAuthAttempted,
    isAuthenticated,
    triggerInteractiveLogin,
  } = useAuthContext()

  // Show loading while attempting silent auth
  if (!silentAuthAttempted) {
    return (
      <div className={styles.container}>
        <Spinner size="medium" label={loadingMessage} />
      </div>
    )
  }

  // If authenticated, render children
  if (isAuthenticated) {
    return <>{children}</>
  }

  // Interaction required - show continue button
  if (requiresInteraction) {
    return (
      <div className={styles.container}>
        <PersonRegular fontSize={48} />
        <Text className={styles.message} size={400}>
          {promptMessage}
        </Text>
        <Button
          appearance="primary"
          icon={<PersonRegular />}
          onClick={triggerInteractiveLogin}
        >
          {buttonText}
        </Button>
        {isTeams && (
          <Text className={styles.teamsHint}>
            Running in Microsoft Teams
          </Text>
        )}
      </div>
    )
  }

  // Fallback: still loading or unknown state
  return (
    <div className={styles.container}>
      <Spinner size="medium" />
    </div>
  )
}
